import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/compat'
import PropTypes from 'prop-types'
import { redirect, sprintf } from '../../helpers/common'
import { CloseIcon } from '../../assets/dynamicIcons'
import { useTranslations } from '../../hooks/useTranslations'
import { DataDog } from '../../datadog'
import classNames from 'classnames'
import styles from './PageTimer.module.scss'

const timeFormat = n => (n < 10 ? '0' : '') + n

const calculateTimeLeft = (expiryDate, timeoutUrl) => {
  const difference = +new Date(expiryDate) - Date.now()

  let timeLeft = {
    hours: 0,
    minutes: 0,
    seconds: 0,
  }

  if (difference > 0) {
    timeLeft = {
      hours: Math.floor(difference / 1000 / 60 / 60),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    }
  } else if (timeoutUrl) {
    DataDog.info(
      `Redirection. Component: PageTimer, redirect url: ${timeoutUrl}`,
    )
    redirect(timeoutUrl)
  }

  return timeLeft
}

const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={styles.stopWatch}
    viewBox="0 0 24 24"
    width="20"
    height="20"
  >
    <g
      fill="none"
      fillRule="evenodd"
      stroke="#1b115c"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="12" cy="13" r="9" />
      <path d="M12 4V2m3-1H9m12 4-1-1m-1 2 1-1" />
      <g id="hand">
        <path d="M12 13L12 8" />
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 13"
          to="360 12 13"
          dur="2s"
          repeatCount="indefinite"
        />
      </g>
    </g>
  </svg>
)

const getBannerText = (timer, l10n) => {
  if (timer.hours > 0) {
    return timer.hours === 1
      ? sprintf(
          l10n.t('Reserved for %s hour and %s mins'),
          timer.hours,
          timeFormat(timer.minutes),
        )
      : sprintf(
          l10n.t('Reserved for %s hours and %s mins'),
          timer.hours,
          timeFormat(timer.minutes),
        )
  }
  return sprintf(
    l10n.t('Reserved for %sm %ss'),
    timer.minutes,
    timeFormat(timer.seconds),
  )
}

const PageTimer = ({
  expiryDate,
  timeoutUrl,
  showHoursInTimer = 'false',
  timeThreshold = 10,
}) => {
  if (!expiryDate) return null

  const l10n = useTranslations()

  const [timer, setTimer] = useState(calculateTimeLeft(expiryDate, timeoutUrl))
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [isTimerVisible, setIsTimerVisible] = useState(false)

  // eslint-disable-next-line react/prop-types
  const RemainingComponent = ({ dataTestId, children }) => (
    <div className={styles.timerWrapper} data-testid={dataTestId}>
      {children}
      <span className={styles.timerText}>
        {l10n.t('Reserved for')}
        {` ${timeFormat(timer.minutes)}:${timeFormat(timer.seconds)}`}&nbsp;
      </span>
    </div>
  )

  const allowToShowPopup = useRef(true)

  const handleClosePopup = () => {
    allowToShowPopup.current = false
    setIsPopupVisible(false)
  }

  useEffect(() => {
    if (
      allowToShowPopup.current &&
      !isPopupVisible &&
      timer.minutes < timeThreshold &&
      timer.hours === 0
    ) {
      setIsPopupVisible(true)
    }

    if (timer.hours === 0 || showHoursInTimer === 'true') {
      setIsTimerVisible(true)
    } else {
      setIsTimerVisible(false)
    }
  }, [timer.minutes, showHoursInTimer])

  useEffect(() => {
    const time = setInterval(() => {
      setTimer(calculateTimeLeft(expiryDate, timeoutUrl))
    }, 1000)

    return () => {
      clearInterval(time)
    }
  }, [])

  return (
    isTimerVisible && (
      <tui-payments-timer
        className={`${styles.paymentTimer} mfe-component`}
        data-testid="page-timer"
      >
        {isPopupVisible && (
          <div className={styles.paymentTimer__popup}>
            <div className={styles.paymentTimer__popupWrapper}>
              <div className={styles.paymentTimer__icon}>
                <ClockIcon />
              </div>
              <div className={styles.paymentTimer__iconWrapper}>
                <RemainingComponent dataTestId="timer-popup" />
              </div>
              <button
                type="button"
                className={styles.popupTimerClose}
                aria-label="Close remaining time pop up"
                onClick={handleClosePopup}
              >
                <CloseIcon color="#1a115a" dataTestId="timer-popup-close" />
              </button>
            </div>
          </div>
        )}
        {timer.hours === 0 && timer.minutes < timeThreshold ? (
          <div className={styles.paymentTimer__reservedTimeWrapper}>
            <div>
              <ClockIcon />
            </div>
            <RemainingComponent dataTestId="timer-sub-header" />
          </div>
        ) : (
          <div data-testid="timer-sub-header" className={styles.timerWrapper}>
            <ClockIcon />
            <span
              className={classNames(
                styles.timerText,
                styles.paymentTimer__bannerTimer,
              )}
            >
              {getBannerText(timer, l10n)}
            </span>
          </div>
        )}
      </tui-payments-timer>
    )
  )
}

PageTimer.propTypes = {
  expiryDate: PropTypes.string,
  timeoutUrl: PropTypes.string,
  showHoursInTimer: PropTypes.string,
  timeThreshold: PropTypes.number,
}

export default PageTimer
